// In-app KPI card with three states: loading / empty / data.
// See docs/design-system/guidelines/dashboards.md for usage rules.

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface BaseProps {
  label: string;
  className?: string;
  /**
   * Optional tooltip content for an info (i) icon rendered next to the
   * label. When omitted, no icon is rendered. Used by the Overview KPIs
   * to explain what each metric represents.
   */
  info?: ReactNode;
}

interface LoadingProps extends BaseProps {
  state: "loading";
}

interface EmptyProps extends BaseProps {
  state: "empty";
  emptyMessage: string;
  /** Single word to italicize in Quarto inside `emptyMessage` (e.g. "arriving"). */
  emptyAccent?: string;
}

interface DataProps extends BaseProps {
  state: "data";
  value: number;
  /** Decimal delta (e.g. 0.184 for +18.4%). null = no comparison available.
   *  When `deltaUnit` is "pp", this is instead the raw decimal
   *  percentage-point delta (e.g. 0.228 for +22.8 pp). */
  delta?: number | null;
  /** How to render the delta:
   *  - "pct" (default) renders as relative percent: "+18.4%"
   *  - "pp" renders as percentage points: "+22.8 pp"
   *  Use "pp" for ratio/percentage KPIs (engagement rate, social share,
   *  conversion rate) so readers don't conflate a relative change with
   *  an absolute one. Use "pct" for count metrics (sessions, events). */
  deltaUnit?: "pct" | "pp";
  /** Optional formatter for the value; defaults to `Intl.NumberFormat`. */
  format?: (n: number) => string;
}

export type MetricCardProps = LoadingProps | EmptyProps | DataProps;

const cardClass = "flex min-h-[148px] flex-col rounded-xl bg-white p-5 ring-1 ring-neutral-950/5";

export function MetricCard(props: MetricCardProps) {
  return (
    <article className={cn(cardClass, props.className)} aria-busy={props.state === "loading"}>
      <div className="flex items-center gap-1.5">
        <p className="truncate text-[11px] font-bold uppercase tracking-wide text-neutral-500">
          {props.label}
        </p>
        {props.info ? <InfoTooltip label={props.label}>{props.info}</InfoTooltip> : null}
      </div>
      {renderBody(props)}
    </article>
  );
}

function InfoTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`About the ${label} metric`}
          className="relative inline-flex size-4 items-center justify-center text-neutral-400 transition-colors hover:text-purple-700 focus-visible:text-purple-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
        >
          <i className="fa-regular fa-circle-info text-xs" aria-hidden="true" />
          <span
            aria-hidden="true"
            className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
          />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{children}</TooltipContent>
    </Tooltip>
  );
}

function renderBody(props: MetricCardProps) {
  if (props.state === "loading") {
    return (
      <>
        <div aria-hidden="true" className="mt-4 h-8 w-32 animate-pulse rounded bg-neutral-100" />
        <div aria-hidden="true" className="mt-3 h-3 w-40 animate-pulse rounded bg-neutral-100" />
        <span className="sr-only">Loading {props.label}…</span>
      </>
    );
  }

  if (props.state === "empty") {
    return (
      <>
        <div className="mt-3 flex items-center gap-2">
          <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-purple-500" />
          <p className="text-sm leading-snug text-neutral-700 text-pretty">
            {renderEmptyMessage(props)}
          </p>
        </div>
      </>
    );
  }

  const formatter = props.format ?? defaultFormat;
  return (
    <>
      <p className="mt-3 font-display text-4xl font-bold tabular-nums leading-none text-neutral-950">
        {formatter(props.value)}
      </p>
      {props.delta !== undefined && props.delta !== null ? (
        <p className="mt-3">
          <DeltaPill delta={props.delta} deltaUnit={props.deltaUnit ?? "pct"} />
        </p>
      ) : null}
    </>
  );
}

function renderEmptyMessage(props: EmptyProps) {
  if (!props.emptyAccent) return props.emptyMessage;
  const idx = props.emptyMessage.indexOf(props.emptyAccent);
  if (idx === -1) return props.emptyMessage;
  const before = props.emptyMessage.slice(0, idx);
  const after = props.emptyMessage.slice(idx + props.emptyAccent.length);
  return (
    <>
      {before}
      <em className="font-serif font-semibold text-purple-700">{props.emptyAccent}</em>
      {after}
    </>
  );
}

function DeltaPill({ delta, deltaUnit }: { delta: number; deltaUnit: "pct" | "pp" }) {
  const positive = delta >= 0;
  const label =
    deltaUnit === "pp"
      ? `${positive ? "+" : ""}${(delta * 100).toFixed(1)} pp`
      : `${positive ? "+" : ""}${(delta * 100).toFixed(1)}%`;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
        positive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700",
      )}
    >
      <i
        aria-hidden="true"
        className={cn("fa-solid text-[10px]", positive ? "fa-arrow-up" : "fa-arrow-down")}
      />
      {label}
    </span>
  );
}

const defaultFormat = (n: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
